import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { uniqueId } from 'lodash';

import { TId } from '@/features/common';
import { useNotifications } from '@/features/notifications';

import { useRunAutomationMutation } from '../../queries';
import { TAutomationTriggerInput, TAutomationTriggerMap } from '../../types';

import {
  TRIGGER_NOTIFICATION_DEFAULTS,
  useBeforeUnloadCheck,
} from './automations-trigger.utils';
import { TriggerNotifications } from './ui';

interface IAutomationsTriggerContext {
  automationTriggers: TAutomationTriggerMap;
  addAutomationTrigger(automationTrigger: TAutomationTriggerInput): void;
  removeAutomationTrigger(id: TId): void;
  runAutomationTrigger(id: TId): void;
}

const AutomationsTriggerContext = createContext<IAutomationsTriggerContext>({
  automationTriggers: new Map(),
  addAutomationTrigger: () => {},
  removeAutomationTrigger: () => {},
  runAutomationTrigger: () => {},
});

export function AutomationsTriggerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { addNotification } = useNotifications();
  const runAutomationMutation = useRunAutomationMutation();

  const [automationTriggers, setAutomationTriggers] =
    useState<TAutomationTriggerMap>(new Map());
  const ranAutomations = useRef<Set<TId>>(new Set());

  const addAutomationTrigger = useCallback(
    (automationTrigger: TAutomationTriggerInput) => {
      const defaults = TRIGGER_NOTIFICATION_DEFAULTS[automationTrigger.type];
      const mergedContent = {
        ...defaults,
        ...automationTrigger,
      };

      setAutomationTriggers((prev) => {
        const next = new Map(prev);
        next.set(uniqueId('automation-trigger-'), mergedContent);
        return next;
      });
    },
    []
  );

  const removeAutomationTrigger = useCallback(
    (id: TId) => {
      setAutomationTriggers((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });

      addNotification({
        type: 'automation_action_success',
        meta: { action: 'cancelled' },
      });
    },
    [addNotification]
  );

  const runAutomationTrigger = useCallback(
    (id: TId) => {
      if (ranAutomations.current.has(id)) return;

      const automationTrigger = automationTriggers.get(id);
      if (!automationTrigger) return;

      runAutomationMutation.mutate(
        {
          workspaceId: automationTrigger.workspaceId,
          jobOpeningId: automationTrigger.jobOpeningId,
          stageId: automationTrigger.stageId,
          automationId: automationTrigger.automationId,
          candidateIds: automationTrigger.candidateIds,
        },
        {
          onSuccess: () => {
            addNotification({
              type: 'automation_run_success',
              meta: {
                message: automationTrigger.successMessage,
              },
            });
          },
          onSettled: () => {
            ranAutomations.current.add(id);
            setAutomationTriggers((prev) => {
              const next = new Map(prev);
              next.delete(id);
              return next;
            });
          },
        }
      );
    },
    [addNotification, automationTriggers, runAutomationMutation]
  );

  useBeforeUnloadCheck(automationTriggers, runAutomationTrigger);

  return (
    <AutomationsTriggerContext.Provider
      value={{
        automationTriggers,
        addAutomationTrigger,
        removeAutomationTrigger,
        runAutomationTrigger,
      }}
    >
      {children}
      <TriggerNotifications />
    </AutomationsTriggerContext.Provider>
  );
}

export function useAutomationsTrigger(): IAutomationsTriggerContext {
  const context = useContext(AutomationsTriggerContext);
  if (context === undefined) {
    throw new Error(
      'useAutomationsTrigger must be used within a AutomationsTriggerContext.Provider'
    );
  }
  return context;
}
